import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { useGenerateCouponCodes, usePromotions, useCreateCoupon } from '../../hooks';
import { toIsoOrUndefined } from '../../lib/datetimeLocal';
import type { ToastSeverity } from '../../lib/useToast';
import type { CouponKind, CouponResponse } from '../../model/types';

export interface CouponFormDrawerProps {
  open: boolean;
  /** Pre-selects and locks the promotion picker (opened from the builder). */
  promotionId?: string;
  onClose: () => void;
  onSaved: (coupon: CouponResponse) => void;
  onResult: (severity: ToastSeverity, message: string) => void;
}

interface Draft {
  promotionId: string;
  name: string;
  kind: CouponKind;
  explicitCode: string;
  codeMask: string;
  maxRedemptionsTotal: string;
  maxRedemptionsPerCustomer: string;
  maxRedemptionsPerCode: string;
  validFrom: string;
  validTo: string;
}

const EMPTY_DRAFT: Draft = {
  promotionId: '',
  name: '',
  kind: 'MULTI_CODE',
  explicitCode: '',
  codeMask: '',
  maxRedemptionsTotal: '',
  maxRedemptionsPerCustomer: '',
  maxRedemptionsPerCode: '',
  validFrom: '',
  validTo: '',
};

/** Preview a masked code — every run of `X` becomes a run of digits/letters as a rough sample. */
function previewMask(mask: string): string {
  if (!mask.trim()) return '';
  return mask.replace(/X/g, () => '0123456789ABCDEFGHJKMNPQRSTUVWXYZ'[Math.floor(Math.random() * 33)]);
}

/** Right-side drawer to create a coupon mechanism for a `requires_coupon` promotion (§8.5). */
export function CouponFormDrawer({ open, promotionId, onClose, onSaved, onResult }: CouponFormDrawerProps) {
  const { t } = useTranslation('promotions');
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const promotions = usePromotions({ size: 200 }, { enabled: open && !promotionId });
  const createCoupon = useCreateCoupon();
  const generateCodes = useGenerateCouponCodes();
  const pending = createCoupon.isPending || generateCodes.isPending;

  useEffect(() => {
    if (open) setDraft({ ...EMPTY_DRAFT, promotionId: promotionId ?? '' });
  }, [open, promotionId]);

  const isValid =
    draft.promotionId.length > 0 &&
    (draft.kind !== 'SINGLE_CODE' || draft.explicitCode.trim().length > 0);

  const handleSave = async () => {
    try {
      const saved = await createCoupon.mutateAsync({
        promotionId: draft.promotionId,
        name: draft.name.trim() || undefined,
        kind: draft.kind,
        codeMask: draft.kind === 'MULTI_CODE' && draft.codeMask.trim() ? draft.codeMask.trim() : undefined,
        maxRedemptionsTotal: draft.maxRedemptionsTotal.trim() ? Number(draft.maxRedemptionsTotal) : undefined,
        maxRedemptionsPerCustomer: draft.maxRedemptionsPerCustomer.trim()
          ? Number(draft.maxRedemptionsPerCustomer)
          : undefined,
        maxRedemptionsPerCode: draft.maxRedemptionsPerCode.trim() ? Number(draft.maxRedemptionsPerCode) : undefined,
        validFrom: toIsoOrUndefined(draft.validFrom),
        validTo: toIsoOrUndefined(draft.validTo),
      });
      // SINGLE_CODE's one code is set via the separate codes/generate endpoint (`CouponRequest`
      // itself has no code field — only `GenerateCodesRequest.explicitCode` does).
      if (draft.kind === 'SINGLE_CODE') {
        await generateCodes.mutateAsync({
          couponId: saved.id,
          body: { count: 1, explicitCode: draft.explicitCode.trim().toUpperCase() },
        });
      }
      onResult('success', t('couponFormDrawer.saved'));
      onSaved(saved);
      onClose();
    } catch (err) {
      onResult('error', (err as { message?: string }).message ?? t('couponFormDrawer.saveError'));
    }
  };

  const maskPreview = draft.kind === 'MULTI_CODE' ? previewMask(draft.codeMask) : '';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100%', sm: 480 }, display: 'flex', flexDirection: 'column' } } }}
    >
      <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t('couponFormDrawer.title')}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }}>
        <Stack spacing={2.5}>
          {!promotionId ? (
            <TextField
              select
              label={t('couponFormDrawer.promotionLabel')}
              required
              value={draft.promotionId}
              onChange={(e) => setDraft((d) => ({ ...d, promotionId: e.target.value }))}
              fullWidth
            >
              {(promotions.data?.content ?? []).map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </TextField>
          ) : null}

          <TextField
            label={t('couponFormDrawer.nameLabel')}
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            fullWidth
          />

          <RadioGroup
            value={draft.kind}
            onChange={(e) => setDraft((d) => ({ ...d, kind: e.target.value as CouponKind }))}
          >
            <FormControlLabel value="SINGLE_CODE" control={<Radio />} label={t('couponFormDrawer.kind.SINGLE_CODE')} />
            <FormControlLabel value="MULTI_CODE" control={<Radio />} label={t('couponFormDrawer.kind.MULTI_CODE')} />
            <Tooltip title={t('couponFormDrawer.personalComingSoon')}>
              <span>
                <FormControlLabel
                  value="PERSONAL"
                  disabled
                  control={<Radio />}
                  label={t('couponFormDrawer.kind.PERSONAL')}
                />
              </span>
            </Tooltip>
          </RadioGroup>

          {draft.kind === 'SINGLE_CODE' ? (
            <TextField
              label={t('couponFormDrawer.explicitCodeLabel')}
              required
              value={draft.explicitCode}
              onChange={(e) => setDraft((d) => ({ ...d, explicitCode: e.target.value.toUpperCase() }))}
              helperText={t('couponFormDrawer.explicitCodeHint')}
              fullWidth
            />
          ) : null}

          {draft.kind === 'MULTI_CODE' ? (
            <TextField
              label={t('couponFormDrawer.codeMaskLabel')}
              value={draft.codeMask}
              onChange={(e) => setDraft((d) => ({ ...d, codeMask: e.target.value.toUpperCase() }))}
              helperText={
                maskPreview
                  ? t('couponFormDrawer.codeMaskPreview', { example: maskPreview })
                  : t('couponFormDrawer.codeMaskHint')
              }
              placeholder="SUM-XXXX-XX"
              fullWidth
            />
          ) : null}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('couponFormDrawer.maxTotalLabel')}
              type="number"
              value={draft.maxRedemptionsTotal}
              onChange={(e) => setDraft((d) => ({ ...d, maxRedemptionsTotal: e.target.value }))}
              slotProps={{ htmlInput: { min: 1 } }}
              fullWidth
            />
            <TextField
              label={t('couponFormDrawer.maxPerCustomerLabel')}
              type="number"
              value={draft.maxRedemptionsPerCustomer}
              onChange={(e) => setDraft((d) => ({ ...d, maxRedemptionsPerCustomer: e.target.value }))}
              slotProps={{ htmlInput: { min: 1 } }}
              fullWidth
            />
          </Stack>
          <TextField
            label={t('couponFormDrawer.maxPerCodeLabel')}
            type="number"
            value={draft.maxRedemptionsPerCode}
            onChange={(e) => setDraft((d) => ({ ...d, maxRedemptionsPerCode: e.target.value }))}
            slotProps={{ htmlInput: { min: 1 } }}
            fullWidth
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('couponFormDrawer.validFromLabel')}
              type="datetime-local"
              value={draft.validFrom}
              onChange={(e) => setDraft((d) => ({ ...d, validFrom: e.target.value }))}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
            />
            <TextField
              label={t('couponFormDrawer.validToLabel')}
              type="datetime-local"
              value={draft.validTo}
              onChange={(e) => setDraft((d) => ({ ...d, validTo: e.target.value }))}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
            />
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
        <Button onClick={onClose} disabled={pending}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSave()}
          disabled={!isValid || pending}
          startIcon={pending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('common:action.create')}
        </Button>
      </Box>
    </Drawer>
  );
}
